// SCR-STAFF-102 — هویت تأییدشده (AP-PAT-02): استعلام شاهکار دستی (US-PAT-020؛ عدم دسترسی سرویس مانع کار نیست — BR-PAT-031)،
// وضعیت و قفل فیلدهای تأییدشده (US-PAT-023) و شناسه‌های اتباع خارجی با کشور (US-PAT-022؛ فقط ۴ نویسه آخر، نمایش کامل با Audit).
// AP-INT-04 (US-PAT-021): استعلام ثبت احوال — مشخصات برگشتی **پیشنهاد**‌اند و تا تأیید کاربر ثبت نمی‌شوند (AC-PAT-021.1)؛
// وضعیت فوت اعلامی سامانه فقط هشدار است و فوت خودکار ثبت نمی‌شود (AC-PAT-021.2).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type IdentifierRequest,
  type CivilRegistryInquiryView,
  type IdentityInquiryView,
  type PatientIdentifierView,
  type PatientView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ConfirmDialog,
  DataTable,
  ErrorState,
  FormDialog,
  KeyValueList,
  SelectRHF,
  TextFieldRHF,
  toJalaliDateTime,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { usePatientInvalidation } from './common';
import { IdentityStatusChip } from './PatientHeader';

const IDENTIFIER_TYPES = ['Passport', 'ForeignNationalId', 'Other'] as const;

const identifierSchema = z.object({
  type: z.enum(IDENTIFIER_TYPES),
  country: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, { error: 'pat:validation.country' }),
  value: z.string().trim().min(1, { error: 'common:validation.required' }).max(64, { error: 'common:validation.too_long' }),
});
type IdentifierForm = z.input<typeof identifierSchema>;

function IdentifierDialog({ patientId, open, onClose }: { patientId: string; open: boolean; onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const invalidate = usePatientInvalidation(patientId);
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError, reset } = useForm<IdentifierForm>({
    resolver: zodResolver(identifierSchema),
    defaultValues: { type: 'Passport', country: '', value: '' },
  });
  useEffect(() => {
    if (open) {
      setSubmitError(undefined);
      reset();
    }
  }, [open, reset]);
  const save = useMutation({
    mutationFn: (body: IdentifierRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/identifiers', {
          params: { path: { id: patientId } },
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['type', 'country', 'value'],
        codeFields: { 'PHX-PAT-0105': 'value', 'PHX-PAT-0301': 'value' },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="pat:identifiers.add"
      screenId="SCR-STAFF-102-IDENTIFIER"
      onSubmit={() =>
        void handleSubmit((form) => save.mutate({ type: form.type, country: form.country.toUpperCase(), value: form.value }))()
      }
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <SelectRHF
        control={control}
        name="type"
        labelKey="pat:identifiers.type"
        fullWidth
        options={IDENTIFIER_TYPES.map((value) => ({ value, labelKey: `pat:identifiers.types.${value}` }))}
      />
      <TextFieldRHF
        control={control}
        name="country"
        labelKey="pat:identifiers.country"
        helperKey="pat:identifiers.country_help"
        required
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', maxLength: 2 } }}
      />
      <TextFieldRHF control={control} name="value" labelKey="pat:identifiers.value" required fullWidth slotProps={{ htmlInput: { dir: 'ltr' } }} />
    </FormDialog>
  );
}

function IdentifierValue({ patientId, identifier, canReveal }: { patientId: string; identifier: PatientIdentifierView; canReveal: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const reveal = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/patients/patients/{id}/identifiers/{identifierId}/value', {
          params: { path: { id: patientId, identifierId: identifier.id } },
        }),
      ),
  });
  return (
    <Stack direction="row" spacing={1} alignItems="center" component="span">
      <bdi dir="ltr" data-testid="identifier-value">
        {reveal.data ? reveal.data.value : t('pat:profile.national_id_masked', { last4: identifier.last4 })}
      </bdi>
      {canReveal && !reveal.data && (
        <Tooltip title={t('pat:profile.show_national_id')}>
          <IconButton size="small" aria-label={t('pat:profile.show_national_id')} onClick={() => reveal.mutate()}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function InquiryResult({ result }: { result: IdentityInquiryView }) {
  const { t } = useTranslation('pat');
  const severity = result.outcome === 'Matched' ? 'success' : result.outcome === 'NoMatch' ? 'warning' : 'info';
  return (
    <Alert severity={severity} data-testid="identity-inquiry-result">
      {t(`pat:identity.outcome.${result.outcome}`)}
      {result.suggestOwnerContact ? ` ${t('pat:identity.suggest_owner')}` : ''}
    </Alert>
  );
}

/**
 * نتیجه استعلام ثبت احوال (AC-PAT-021.1، 021.2): هشدار فوت جداگانه، مشخصات پیشنهادی با دکمه تأیید، و پیام نتیجه.
 * مشخصات تا فشردن «ثبت مشخصات پیشنهادی» روی پرونده نمی‌نشینند.
 */
function CivilRegistryResult({
  patientId,
  result,
  canWrite,
}: {
  patientId: string;
  result: CivilRegistryInquiryView;
  canWrite: boolean;
}) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const invalidate = usePatientInvalidation(patientId);
  const idempotency = useIdempotencyKey();
  const accept = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}:accept-civil-registry', {
          params: { path: { id: patientId } },
          body: {
            firstName: result.firstName ?? null,
            lastName: result.lastName ?? null,
            fatherName: result.fatherName ?? null,
            gender: result.gender ?? null,
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error ?? undefined);
      invalidate();
    },
  });
  const severity = result.outcome === 'Found' ? 'success' : result.outcome === 'BirthDateMismatch' ? 'warning' : 'info';
  const hasSuggestion = Boolean(result.firstName || result.lastName || result.fatherName);

  return (
    <Stack spacing={1} data-testid="civil-registry-result">
      {result.deceasedWarning && (
        <Alert severity="warning" data-testid="civil-registry-deceased">
          {t('pat:identity.civil_registry_deceased')}
        </Alert>
      )}
      <Alert severity={severity}>{t(`pat:identity.civil_registry_outcome.${result.outcome}`)}</Alert>
      {hasSuggestion && (
        <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="civil-registry-suggestion">
          <Typography variant="subtitle1">{t('pat:identity.civil_registry_title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('pat:identity.civil_registry_help')}
          </Typography>
          <KeyValueList
            columns={3}
            items={[
              { labelKey: 'pat:profile.first_name', value: result.firstName },
              { labelKey: 'pat:profile.last_name', value: result.lastName },
              { labelKey: 'pat:identity.father_name', value: result.fatherName },
            ]}
          />
          {canWrite && (
            <Button
              sx={{ mt: 1 }}
              onClick={() => accept.mutate()}
              disabled={accept.isPending}
              data-testid="civil-registry-accept"
            >
              {t('pat:identity.civil_registry_accept')}
            </Button>
          )}
          {accept.error ? <ErrorState error={accept.error} compact /> : null}
        </Paper>
      )}
    </Stack>
  );
}

export function PatientIdentityPanel({ patient, canWrite }: { patient: PatientView; canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi } = useTenantSession();
  const invalidate = usePatientInvalidation(patient.id);
  const idempotency = useIdempotencyKey();
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<PatientIdentifierView | null>(null);
  const verify = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}:verify-identity', {
          params: { path: { id: patient.id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error ?? undefined);
      invalidate();
    },
  });
  const civilRegistry = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}:inquire-civil-registry', {
          params: { path: { id: patient.id } },
        }),
      ),
    onSettled: () => invalidate(),
  });
  const remove = useMutation({
    mutationFn: (identifier: PatientIdentifierView) =>
      unwrap(
        tenantApi.DELETE('/api/v1/patients/patients/{id}/identifiers/{identifierId}', {
          params: { path: { id: patient.id, identifierId: identifier.id } },
        }),
      ),
    onSuccess: () => {
      invalidate();
      setDeleting(null);
    },
  });
  const identity = patient.identity;
  const columns: DataTableColumn<PatientIdentifierView>[] = [
    { id: 'type', headerKey: 'pat:identifiers.type', cell: (row) => t(`pat:identifiers.types.${row.type}`) },
    { id: 'country', headerKey: 'pat:identifiers.country', cell: (row) => <bdi dir="ltr">{row.country}</bdi> },
    { id: 'value', headerKey: 'pat:identifiers.value', cell: (row) => <IdentifierValue patientId={patient.id} identifier={row} canReveal={canWrite} /> },
    {
      id: 'actions',
      headerKey: 'pat:identifiers.delete',
      cell: (row) =>
        canWrite ? (
          <IconButton aria-label={t('pat:identifiers.delete')} onClick={() => setDeleting(row)}>
            <DeleteOutlineIcon />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="identity-panel">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:identity.title')}
        </Typography>
        {canWrite && (
          <Button
            startIcon={<VerifiedUserOutlinedIcon />}
            onClick={() => verify.mutate()}
            disabled={verify.isPending || patient.isTemporary}
            data-testid="identity-verify"
          >
            {t('pat:identity.verify')}
          </Button>
        )}
        {canWrite && (
          <Button
            startIcon={<BadgeOutlinedIcon />}
            onClick={() => civilRegistry.mutate()}
            disabled={civilRegistry.isPending || patient.isTemporary}
            data-testid="identity-civil-registry"
          >
            {t('pat:identity.civil_registry')}
          </Button>
        )}
      </Stack>
      <Stack spacing={1.5}>
        <KeyValueList
          columns={3}
          items={[
            { labelKey: 'pat:identity.status_label', value: <IdentityStatusChip status={identity.status} /> },
            { labelKey: 'pat:identity.verified_at', value: identity.verifiedAt ? toJalaliDateTime(identity.verifiedAt) : null },
            { labelKey: 'pat:identity.checked_at', value: identity.checkedAt ? toJalaliDateTime(identity.checkedAt) : null },
          ]}
        />
        {identity.locked && (
          <Alert severity="info" data-testid="identity-locked">
            {identity.canOverride ? t('pat:identity.locked_override') : t('pat:identity.locked')}
          </Alert>
        )}
        {verify.data && <InquiryResult result={verify.data} />}
        {verify.error ? <ErrorState error={verify.error} compact /> : null}
        {civilRegistry.data && <CivilRegistryResult patientId={patient.id} result={civilRegistry.data} canWrite={canWrite} />}
        {civilRegistry.error ? <ErrorState error={civilRegistry.error} compact /> : null}
        {identity.civilRegistryDeceasedWarning && !civilRegistry.data ? (
          <Alert severity="warning" data-testid="civil-registry-deceased-standing">
            {t('pat:identity.civil_registry_deceased')}
          </Alert>
        ) : null}
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {t('pat:identifiers.title')}
          </Typography>
          {canWrite && (
            <Button startIcon={<AddIcon />} onClick={() => setAdding(true)} data-testid="identifier-add">
              {t('pat:identifiers.add')}
            </Button>
          )}
        </Stack>
        <DataTable
          captionKey="pat:identifiers.title"
          columns={columns}
          rows={patient.identifiers}
          getRowId={(row) => row.id}
          emptyTitleKey="pat:identifiers.empty"
        />
      </Stack>
      <IdentifierDialog patientId={patient.id} open={adding} onClose={() => setAdding(false)} />
      <ConfirmDialog
        open={deleting !== null}
        titleKey="pat:identifiers.delete"
        messageKey="pat:identifiers.delete_confirm"
        destructive
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </Paper>
  );
}
